/**
 * The line a supplier's price is recorded AGAINST when a customer asks to add an item mid-revision.
 *
 * ---- The gap this closes ----
 *
 * A revision request to add an item lands on an item nobody has priced. Measured live 2026-09-04
 * on `1903010001` (COVERALL WITH REFLECTIVE TAPE & LOGO VORTEX 240GSM ORANGE SIZE M): no
 * `Supplier Quotation Item` row anywhere carries that code, and its item group
 * `01.BOILER SUIT WITH LOGO VORTEX` has no `Item Group Supplier` row either. So the supplier
 * picker truthfully reports "no past price" AND "no suppliers linked", and both of its escapes
 * have to work — an RFQ round trip, or recording a price the supplier gave on the phone.
 *
 * Recording it was the one that did not. `RecordQuotationModal` takes ENQUIRY lines, and the item
 * a customer has just asked to add is by definition not on the enquiry — so seeding it from the
 * enquiry gave either every unrelated line (before this) or none at all.
 *
 * ---- Why a synthesized line is safe here ----
 *
 * `enquiry_row_no` is the through-key `comparison.get_comparison` joins offers to enquiry rows on
 * (`manualSupplierQuotation.ts` records the measurement), and the synthesized line has none — so
 * the quotation it produces is invisible in the comparison. That is CORRECT rather than a
 * compromise: an item that is not on the enquiry has no row in the comparison to appear beside.
 *
 * What the recorded price does reach is `useItemQuoteHistory`, which filters `Supplier Quotation
 * Item` on `item_code` alone. So the price the purchaser keys in here is exactly the past price
 * the supplier picker said was missing, and re-opening the picker now ranks that supplier on it.
 *
 * When the item IS already on the enquiry the real line is used untouched, `cust_sno` and all —
 * that price belongs in the comparison and must keep its key.
 */

import type { EnquiryItem, NewEnquiryItem, RevisionRequest } from './types'

/** Only the field this needs, so any partially-loaded catalogue item is still usable. */
interface BaseUomSource {
  base_uom?: string | null
}

export function lineForRevisionRequest(
  request: RevisionRequest,
  enquiryLines: readonly EnquiryItem[],
  item: BaseUomSource | null | undefined,
): EnquiryItem[] {
  const code = request.item_code
  // No item named, nothing to price — the caller shows its own explanation.
  if (!code) return []

  const onEnquiry = enquiryLinesFor(request, enquiryLines)
  if (onEnquiry.length > 0) return onEnquiry

  return [
    {
      /*
        Namespaced, and never sent: `line_id` is only the key `RecordQuotationModal` holds typed
        rates under. Prefixed so it can never collide with a real `Enquiry Item.name`.
      */
      line_id: `revision:${request.id}`,
      // Blank on purpose — see the header. There is no enquiry row for this item.
      cust_sno: '',
      raw_text: request.detail,
      // Prints as the request was worded — the same text, since nobody has edited it yet.
      description: request.detail,
      customer_uom: '',
      conversion_factor: 1,
      qty_as_per_stock_uom: 0,
      item_code: code,
      // Chosen by a person from the catalogue, not matched by the extractor.
      confidence: 1,
      // A request that names an item but no quantity still means one of them.
      qty: request.qty ?? 1,
      uom: item?.base_uom || 'Nos',
      // `RecordQuotationModal` prices RFQ-routed lines only; a contract line is already priced.
      route: 'rfq',
      selected_rate: 0,
      suppliers: [],
      note: null,
    item_creation_req_ref: null,
    },
  ]
}

/** The enquiry's own lines for a request's item — the one rule for "is it already on there". */
export function enquiryLinesFor(
  request: RevisionRequest,
  enquiryLines: readonly EnquiryItem[],
): EnquiryItem[] {
  const code = request.item_code
  if (!code) return []
  return enquiryLines.filter((l) => l.item_code === code)
}

/**
 * What "Send an RFQ" has to do before the RFQ builder can be any use.
 *
 * An RFQ is built from ENQUIRY lines — `RFQ Item` carries `enquiry_row_no` and the RFQ belongs to
 * the enquiry — so an item the customer has only asked for in a revision cannot be sent to anyone
 * until it is a line on the enquiry. Verified live 2026-09-04 on ENQ-2026-00027: adding the row
 * (`update_enquiry`), creating the RFQ against it (`rfq.create_rfq` → RFQ-2026-00029) and filing
 * the reply (`create_supplier_quotation` → SQ-2026-00278) put the offer on the comparison's row 4.
 *
 * Until now the button navigated and left the purchaser to add the line by hand, from memory, on
 * a screen with 18,000 items in its catalogue.
 *
 * The UOM is NOT defaulted. `lineForRevisionRequest` above may fall back to `Nos` because its line
 * is only ever displayed and priced; this one is WRITTEN to the enquiry, and a plausible-but-wrong
 * unit silently misstates every quantity the supplier is asked to price. Refused instead.
 */
export type RfqSourcingPlan =
  | { kind: 'already-on-enquiry' }
  | { kind: 'add'; line: NewEnquiryItem }
  | { kind: 'cannot'; reason: string }

export function planRfqSourcing(
  request: RevisionRequest,
  enquiryLines: readonly EnquiryItem[],
  item: BaseUomSource | null | undefined,
): RfqSourcingPlan {
  const code = request.item_code
  if (!code) {
    return { kind: 'cannot', reason: "This request doesn't name a catalogue item, so there is nothing to send." }
  }
  if (enquiryLinesFor(request, enquiryLines).length > 0) return { kind: 'already-on-enquiry' }

  const uom = item?.base_uom
  if (!uom) {
    return { kind: 'cannot', reason: `Still loading ${code} from the catalogue — try again in a moment.` }
  }

  return { kind: 'add', line: { item_code: code, qty: request.qty ?? 1, uom } }
}
